// src/api/features/currencies/queries.ts

import { useQuery, type QueryClient } from "@tanstack/react-query";

import { listCurrencies, getCurrency, listPublicCurrencies } from "./api";

import type {
  Currency,
  CurrencyListQuery,
  PaginatedCurrencyItems,
  PublicCurrency,
} from "./types";

/* ======================================================
 * Query Keys
 * ====================================================== */

export const CurrenciesKeys = {
  all: ["currencies"] as const,
  list: (query: CurrencyListQuery = {}) => ["currencies", "list", query] as const,
  publicList: (query: { q?: string; limit?: number } = {}) =>
    ["currencies", "public-list", query] as const,
  detail: (id: number | string) =>
    ["currencies", "detail", String(id)] as const,
};

/* ======================================================
 * LIST hook – GET /currencies
 * Returns: PaginatedCurrencyItems (unwrapped)
 * ====================================================== */

export function useCurrenciesList(
  query: CurrencyListQuery = {},
  options?: { enabled?: boolean }
) {
  return useQuery<PaginatedCurrencyItems, Error>({
    queryKey: CurrenciesKeys.list(query),
    queryFn: () => listCurrencies(query),
    enabled: options?.enabled ?? true,

    // v5 pattern: keep previous page data while refetching
    placeholderData: (previousData) => previousData,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/* ======================================================
 * DETAIL hook – GET /currencies/:id
 * Returns: Currency
 * ====================================================== */

export function useCurrencyDetail(id: number | null) {
  const enabled = id != null;

  return useQuery<Currency, Error>({
    queryKey: enabled
      ? CurrenciesKeys.detail(id as number)
      : ["currencies", "detail", "disabled"],
    queryFn: () => {
      if (id == null) {
        // never runs due to enabled:false, just for TS
        return Promise.reject(new Error("Currency id is required"));
      }
      return getCurrency(id);
    },
    enabled,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

export function usePublicCurrenciesList(
  query: { q?: string; limit?: number } = {},
  options?: { enabled?: boolean }
) {
  return useQuery<PublicCurrency[], Error>({
    queryKey: CurrenciesKeys.publicList(query),
    queryFn: () => listPublicCurrencies(query),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

/* ======================================================
 * Prefetch helpers (optional)
 * ====================================================== */

export function prefetchCurrencies(
  qc: QueryClient,
  query: CurrencyListQuery = {}
) {
  return qc.prefetchQuery({
    queryKey: CurrenciesKeys.list(query),
    queryFn: () => listCurrencies(query),
    staleTime: 30_000,
  });
}

export function prefetchCurrency(qc: QueryClient, id: number) {
  return qc.prefetchQuery({
    queryKey: CurrenciesKeys.detail(id),
    queryFn: () => getCurrency(id),
    staleTime: 60_000,
  });
}
